import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CategoryBadge, ConfidenceBar } from "@/components/CommentBits";
import Layout from "@/components/Layout";
import { NextSteps, ComponentBars, scoreColor } from "@/components/BrandBits";
import api from "@/lib/api";
import { timeAgo } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import {
  TrendingUp, TrendingDown, Minus, Building2, Users, Activity, Link2, ListChecks, ShieldAlert, ShieldCheck, Clock, ArrowUpRight,
} from "lucide-react";

const tone = (name, stop) => `rgb(var(--c-${name}-${stop}))`;
const TOOLTIP = {
  contentStyle: { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "hsl(var(--foreground))" },
  labelStyle: { color: "hsl(var(--muted-foreground))" },
};

// Severity rail on the left edge of each priority-queue row, by AI confidence.
const severity = (v) => (v >= 0.85 ? "border-l-red-500 bg-red-50/60" : v >= 0.7 ? "border-l-amber-500 bg-amber-50/60" : "border-l-slate-300 bg-slate-50");

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 min-w-0">
      <Icon className="w-4 h-4 text-brass-500 shrink-0" strokeWidth={1.5} />
      <div className="min-w-0">
        <p className="eyebrow truncate">{label}</p>
        <p className="font-heading text-2xl font-medium text-slate-900 leading-tight tabular-nums">{value}</p>
      </div>
    </div>
  );
}

const ACTION_STYLE = {
  create: "bg-green-50 text-green-600",
  update: "bg-blue-50 text-blue-600",
  delete: "bg-red-50 text-red-600",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState(null);
  const [queue, setQueue] = useState(null);
  const [health, setHealth] = useState(null);
  const [actions, setActions] = useState(null);

  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  // Trend and priority queue are per-client; the agency view has no single client to scope to.
  useEffect(() => {
    if (!user || isSuper) return;
    api.get("/reports/summary?days=14").then(({ data }) => setSeries(data.timeseries || [])).catch(() => setSeries([]));
    api.get("/comments?status=needs_review").then(({ data }) => setQueue(data)).catch(() => setQueue([]));
    api.get("/brand/health?days=14").then(({ data }) => setHealth(data)).catch(() => setHealth({ score: null, trend: [] }));
    api.get("/brand/actions").then(({ data }) => setActions(data)).catch(() => setActions([]));
  }, [user, isSuper]);

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-8">
        <div>
          <p className="eyebrow mb-3">
            {isSuper ? "Bloom Dev Labs · Agency overview" : user?.client?.name}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl text-slate-900 tracking-tight">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <div className="rule-brass mt-5" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 bg-card rounded-2xl border border-slate-100 shadow-card divide-slate-100 lg:divide-x [&>*:nth-child(n+3)]:border-t lg:[&>*:nth-child(n+3)]:border-t-0 [&>*]:border-slate-100" data-testid="stat-strip">
          {isSuper ? (
            <>
              <StatCard index={0} icon={Building2} label="Total clients" value={stats?.total_clients ?? "—"} />
              <StatCard index={1} icon={ShieldCheck} label="Active clients" value={stats?.active_clients ?? "—"} />
              <StatCard index={2} icon={Users} label="Total users" value={stats?.total_users ?? "—"} />
              <StatCard index={3} icon={Activity} label="Platforms" value="FB · IG" />
            </>
          ) : (
            <>
              <StatCard index={0} icon={Link2} label="Connected accounts" value={stats?.connected_accounts ?? 0} />
              <StatCard index={1} icon={ListChecks} label="Pending review" value={stats?.pending_review ?? 0} />
              <StatCard index={2} icon={ShieldAlert} label="Removed today" value={stats?.removed_today ?? 0} />
              <StatCard index={3} icon={Users} label="Team members" value={stats?.team_size ?? "—"} />
            </>
          )}
        </div>

        {!isSuper && (
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2 bg-card rounded-2xl p-6 border border-slate-100 shadow-card" data-testid="brand-health-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-slate-900">Brand health</h2>
                <Link to="/brand" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                  Details <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
              {health === null ? (
                <div className="skeleton h-[160px]" />
              ) : health.score == null ? (
                <p className="text-sm text-slate-400 py-12 text-center">Score appears once comments are ingested.</p>
              ) : (
                <>
                  <div className="flex items-end gap-3">
                    <span className="font-heading text-6xl font-medium leading-none tabular-nums" style={{ color: scoreColor(health.score) }} data-testid="dash-brand-score">{health.score}</span>
                    <span className="text-slate-400 mb-1">/ 100</span>
                    <span className="ml-auto mb-1 inline-flex items-center gap-1 text-xs text-slate-500">
                      {health.delta_7d == null || health.delta_7d === 0 ? <Minus className="w-3.5 h-3.5" /> : health.delta_7d > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {health.delta_7d == null ? "no 7-day comparison" : `${health.delta_7d > 0 ? "+" : ""}${health.delta_7d} vs last week`}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={90}>
                    <LineChart data={health.trend} margin={{ top: 8, bottom: 0, left: 0, right: 0 }}>
                      <YAxis domain={[0, 100]} hide />
                      <Tooltip {...TOOLTIP} />
                      <Line type="monotone" dataKey="score" name="Score" stroke={scoreColor(health.score)} strokeWidth={2} dot={false} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-5 pt-5 border-t border-slate-100">
                    <ComponentBars components={health.components} />
                  </div>
                </>
              )}
            </div>

            <div className="lg:col-span-3 bg-card rounded-2xl p-6 border border-slate-100 shadow-card" data-testid="next-steps-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-slate-900">Recommended next steps</h2>
                <Link to="/brand" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                  See all <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
              {actions === null ? (
                <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="skeleton h-[96px]" />)}</div>
              ) : (
                <NextSteps actions={actions} limit={2} />
              )}
              <Link to="/content" data-testid="content-teaser" className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                Need something to post? Get ideas in Content Studio <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}

        {!isSuper && (
          <div className="grid lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 bg-card rounded-2xl p-6 border border-slate-100 shadow-card" data-testid="trend-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-slate-900">Comment volume</h2>
                <span className="eyebrow">Last 14 days</span>
              </div>
              {series === null ? (
                <div className="skeleton h-[220px]" />
              ) : series.length === 0 ? (
                <p className="text-sm text-slate-400 py-16 text-center">No comments in this period.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={series} margin={{ left: -20, right: 8, top: 5 }}>
                    <defs>
                      <linearGradient id="dash-total" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={tone("accent", 600)} stopOpacity={0.28} />
                        <stop offset="95%" stopColor={tone("accent", 600)} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={tone("stone", 200)} vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11, fill: tone("stone", 500) }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: tone("stone", 500) }} axisLine={false} tickLine={false} />
                    <Tooltip {...TOOLTIP} />
                    <Area type="monotone" dataKey="total" name="All comments" stroke={tone("accent", 600)} fill="url(#dash-total)" strokeWidth={1.75} />
                    <Area type="monotone" dataKey="removed" name="Removed" stroke={tone("oxblood", 500)} fill="none" strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="lg:col-span-2 bg-card rounded-2xl p-6 border border-slate-100 shadow-card" data-testid="priority-queue">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-slate-900">Priority queue</h2>
                <Link to="/review" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
                  See all <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
              {queue === null ? (
                <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[68px]" />)}</div>
              ) : queue.length === 0 ? (
                <p className="text-sm text-slate-400 py-12 text-center">Nothing waiting for review.</p>
              ) : (
                <ul className="space-y-2">
                  {[...queue].sort((a, b) => (b.ai?.confidence || 0) - (a.ai?.confidence || 0)).slice(0, 4).map((c) => (
                    <li key={c.id} className={`rounded-lg border-l-2 px-3 py-2.5 ${severity(c.ai?.confidence || 0)}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] font-medium text-slate-800 truncate">{c.author_name}</span>
                        <CategoryBadge category={c.ai?.category} />
                      </div>
                      <p className="text-xs text-slate-600 line-clamp-2 mb-1.5">{c.text}</p>
                      <ConfidenceBar value={c.ai?.confidence} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div>
          {/* Recent activity */}
          <div className="lg:col-span-2 bg-card rounded-2xl p-6 border border-slate-100 shadow-card">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
              <h2 className="font-heading text-xl text-slate-900">Recent activity</h2>
              <span className="eyebrow">Audit trail</span>
            </div>
            {stats?.recent_activity?.length ? (
              <div className="space-y-1">
                {stats.recent_activity.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 py-2.5 px-2 rounded-xl hover:bg-slate-50 transition-colors" data-testid="activity-row">
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.1em] min-w-[5.5rem] whitespace-nowrap text-center px-3 py-1 rounded-sm ${ACTION_STYLE[a.action] || "bg-slate-100 text-slate-500"}`}>
                      {a.action}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 truncate">
                        <span className="font-medium">{a.actor_email}</span> {a.action}d a {a.entity_type}
                      </p>
                    </div>
                    <span className="flex items-center gap-1 text-xs text-slate-400 shrink-0">
                      <Clock className="w-3 h-3" /> {timeAgo(a.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 py-8 text-center">No activity yet.</p>
            )}
          </div>

        </div>
      </div>
    </Layout>
  );
}
